import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav from '../components/AdminNav';
import { useLang } from '../useLang';
import '../design-system.css';

function Messages() {
    const navigate = useNavigate();
    const { t } = useLang();
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [conversations, setConversations] = useState([]);
    const [activeConv, setActiveConv] = useState(null);
    const [convMessages, setConvMessages] = useState([]);
    const [newMsg, setNewMsg] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [showNewConv, setShowNewConv] = useState(false);
    const [searchUser, setSearchUser] = useState('');
    const messagesEndRef = useRef(null);

    useEffect(() => {
        const init = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) { navigate('/admin'); return; }
            const { data: userData } = await supabase
                .from('users')
                .select('username, role, avatar_url')
                .eq('email', data.session.user.email)
                .single();
            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setAvatarUrl(userData.avatar_url || '');
                fetchConversations(userData.username);

                const { data: users } = await supabase
                    .from('users')
                    .select('username, first_name, last_name')
                    .neq('username', userData.username);
                if (users) setAllUsers(users);
            }
            if (Notification.permission === 'default') {
                Notification.requestPermission();
            }
        };
        init();
    }, [navigate]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [convMessages]);

    useEffect(() => {
        if (!activeConv || !username) return;

        const sub = supabase
            .channel(`conv-${activeConv.id}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'conversation_messages',
                filter: `conversation_id=eq.${activeConv.id}`,
            }, payload => {
                setConvMessages(prev => {
                    if (prev.find(m => m.id === payload.new.id)) return prev;
                    return [...prev, payload.new];
                });

                if (payload.new.from_username !== username) {
                    if (Notification.permission === 'granted' && document.hidden) {
                        new Notification(`New message from @${payload.new.from_username}`, {
                            body: payload.new.body,
                            icon: '/winston-ie-app-image.png',
                        });
                    }
                }
            })
            .subscribe();

        return () => supabase.removeChannel(sub);
    }, [activeConv, username]);

    const fetchConversations = async (uname) => {
        const { data } = await supabase
            .from('conversations')
            .select('*, conversation_messages(id, read, from_username)')
            .or(`participant_1.eq.${uname},participant_2.eq.${uname}`)
            .order('last_message_at', { ascending: false });

        if (data) {
            const withUnread = data.map(conv => ({
                ...conv,
                unread: conv.conversation_messages?.some(m => !m.read && m.from_username !== uname)
            }));
            setConversations(withUnread);
        }
    };

    const openConversation = async (conv) => {
        setActiveConv(conv);
        const { data } = await supabase
            .from('conversation_messages')
            .select('*')
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: true });
        if (data) setConvMessages(data);

        await supabase
            .from('conversation_messages')
            .update({ read: true })
            .eq('conversation_id', conv.id)
            .neq('from_username', username);

        setConversations(prev => prev.map(c =>
            c.id === conv.id ? { ...c, unread: false } : c
        ));
    };

    const sendMessage = async () => {
        if (!newMsg.trim() || !activeConv) return;
        const msg = newMsg.trim();
        setNewMsg('');

        const { data } = await supabase.from('conversation_messages').insert({
            conversation_id: activeConv.id,
            from_username: username,
            body: msg,
        }).select().single();

        if (data) setConvMessages(prev => [...prev, data]);

        await supabase.from('conversations')
            .update({ last_message_at: new Date().toISOString() })
            .eq('id', activeConv.id);
    };

    const startConversation = async (targetUsername) => {
        const existing = conversations.find(c =>
            (c.participant_1 === username && c.participant_2 === targetUsername) ||
            (c.participant_1 === targetUsername && c.participant_2 === username)
        );

        if (existing) {
            openConversation(existing);
            setShowNewConv(false);
            setSearchUser('');
            return;
        }

        const { data } = await supabase.from('conversations').insert({
            participant_1: username,
            participant_2: targetUsername,
        }).select().single();

        if (data) {
            setConversations(prev => [data, ...prev]);
            openConversation(data);
        }
        setShowNewConv(false);
        setSearchUser('');
    };

    const getOtherParticipant = (conv) =>
        conv.participant_1 === username ? conv.participant_2 : conv.participant_1;

    const filteredUsers = allUsers.filter(u =>
        u.username.toLowerCase().includes(searchUser.toLowerCase()) ||
        `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(searchUser.toLowerCase())
    );

    const formatTime = (ts) => new Date(ts).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', hour12: false });
    const formatDate = (ts) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    return (
        <div className="w-page" style={{ height: '100vh', overflow: 'hidden' }}>
            <AdminNav username={username} role={role} avatarUrl={avatarUrl} onLogout={async () => { await supabase.auth.signOut(); navigate('/'); }} activePath="/admin/messages" />

            <div className="w-messages-grid" style={{ flex: 1, display: 'grid', gridTemplateColumns: '300px 1fr', overflow: 'hidden' }}>

                <div className={`w-messages-list ${activeConv ? 'w-messages-list-hidden-mobile' : ''}`} style={{ borderRight: '1px solid var(--w-border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--w-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="w-eyebrow" style={{ margin: 0 }}>{t('messagesTitle')}</span>
                        <button className="w-btn-primary" style={{ padding: '6px 12px', fontSize: '11px' }} onClick={() => setShowNewConv(true)}>{t('newConv')}</button>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {conversations.length === 0 && (
                            <p style={{ fontSize: '12px', color: 'var(--w-muted)', textAlign: 'center', marginTop: '40px', fontStyle: 'italic' }}>{t('noConversations')}</p>
                        )}
                        {conversations.map(conv => {
                            const other = getOtherParticipant(conv);
                            const isActive = activeConv?.id === conv.id;
                            return (
                                <div
                                    key={conv.id}
                                    onClick={() => openConversation(conv)}
                                    style={{ padding: '14px 22px', borderBottom: '1px solid var(--w-border)', cursor: 'pointer', background: isActive ? 'var(--w-bg-soft)' : 'var(--w-bg)', transition: 'background 0.15s' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--w-ink)' }}>@{other}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '10px', color: 'var(--w-muted)' }}>{formatDate(conv.last_message_at)}</span>
                                            {conv.unread && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--w-ink)' }} />}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={`w-messages-thread ${!activeConv ? 'w-messages-thread-hidden-mobile' : ''}`} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {!activeConv ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--w-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                            {t('selectConversation')}
                        </div>
                    ) : (
                        <>
                            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--w-border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <button
                                    className="w-messages-back"
                                    onClick={() => setActiveConv(null)}
                                    aria-label="Retour"
                                >
                                    ←
                                </button>
                                <div className="w-profile-avatar-sq" style={{ width: '34px', height: '34px', borderRadius: '10px', fontSize: '12px' }}>
                                    {getOtherParticipant(activeConv)[0].toUpperCase()}
                                </div>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--w-ink)' }}>@{getOtherParticipant(activeConv)}</span>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {convMessages.map((msg, i) => {
                                    const isMe = msg.from_username === username;
                                    const showDate = i === 0 || formatDate(convMessages[i - 1].created_at) !== formatDate(msg.created_at);
                                    return (
                                        <div key={msg.id}>
                                            {showDate && (
                                                <div style={{ textAlign: 'center', fontSize: '10px', color: 'var(--w-muted)', letterSpacing: '0.06em', margin: '12px 0 4px' }}>
                                                    {formatDate(msg.created_at)}
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                                <div style={{
                                                    maxWidth: '65%',
                                                    padding: '9px 14px',
                                                    borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                    background: isMe ? 'var(--w-ink)' : 'var(--w-bg-soft)',
                                                    color: isMe ? 'white' : 'var(--w-ink)',
                                                    fontSize: '13px',
                                                    lineHeight: 1.5,
                                                }}>
                                                    {msg.body}
                                                    <div style={{ fontSize: '10px', color: isMe ? 'rgba(255,255,255,0.5)' : 'var(--w-muted)', marginTop: '3px', textAlign: 'right' }}>
                                                        {formatTime(msg.created_at)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--w-border)', display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    value={newMsg}
                                    onChange={e => setNewMsg(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                                    placeholder={t('writeMessage')}
                                    style={{ flex: 1, padding: '10px 16px', border: '1px solid var(--w-border)', borderRadius: '20px', fontSize: '13px', outline: 'none', fontFamily: 'inherit' }}
                                />
                                <button className="w-btn-primary" style={{ borderRadius: '20px' }} onClick={sendMessage}>
                                    {t('send')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {showNewConv && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'white', borderRadius: '16px', width: 'min(380px, calc(100vw - 48px))', overflow: 'hidden' }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--w-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <p style={{ fontSize: '13.5px', fontWeight: 600, margin: 0 }}>{t('newConversation')}</p>
                            <button onClick={() => { setShowNewConv(false); setSearchUser(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'var(--w-muted)' }}>✕</button>
                        </div>
                        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
                            <input
                                value={searchUser}
                                onChange={e => setSearchUser(e.target.value)}
                                placeholder={t('searchUser')}
                                autoFocus
                                style={{ padding: '9px 12px', border: '1px solid var(--w-border)', borderRadius: '8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit' }}
                            />
                            {filteredUsers.map(u => (
                                <div
                                    key={u.username}
                                    onClick={() => startConversation(u.username)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'var(--w-bg-soft)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <div className="w-profile-avatar-sq" style={{ width: '32px', height: '32px', borderRadius: '9px', fontSize: '11px' }}>
                                        {u.username[0].toUpperCase()}
                                    </div>
                                    <div>
                                        <p style={{ fontSize: '13px', fontWeight: 600, margin: 0, color: 'var(--w-ink)' }}>{u.first_name} {u.last_name}</p>
                                        <p style={{ fontSize: '11px', color: 'var(--w-muted)', margin: 0 }}>@{u.username}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Messages;